import React, { useMemo } from "react";
import { every, map, reduce } from "lodash";
import StreamChart from "@charty/stream-chart";
import { AbstractDate } from "@linkfluence/models/date";
import { DatavizStreamChart } from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { SearchFragmentDateTerm } from "@radarly/models/search";
import { useRangeFromFragment } from "../../data";
import Empty from "../../empty";
import { computeBucketDataByDate } from "../computation";

export interface SharedWidgetFieldStreamChartInnerProps {
  loading: boolean;
  data: SearchFragmentDateTerm["data"];
  fragment: SearchFragmentDateTerm;
  dataviz: DatavizStreamChart;
  facet: FullFacet;
  onClickLegend?: (uid: string | number) => void;
  onClickElement?: (
    uid: string | number,
    range: { from: Date; to: Date }
  ) => void;
  className?: string;
  disableAnimation?: boolean;
  nowDate?: AbstractDate;
}

export default function SharedWidgetFieldStreamChartInner({
  data,
  loading,
  fragment,
  facet,
  onClickLegend,
  onClickElement,
  className,
  disableAnimation,
  nowDate,
  dataviz,
}: SharedWidgetFieldStreamChartInnerProps) {
  const metric = fragment.request.aggs.date.metrics[0];
  const range = useRangeFromFragment(fragment, loading, nowDate);
  const formattedData = useMemo(() => {
    return map(computeBucketDataByDate(data, facet.id, metric), (entry) => {
      return reduce(
        facet.values,
        (acc, value) => {
          acc[value.label] = entry.data[value.id] || 0;
          return acc;
        },
        {}
      );
    });
  }, [data, facet.id, facet.values, metric]);

  const colors = useMemo(() => {
    return map(facet.values, "color");
  }, [facet]);
  const emptyData = useMemo(
    () => every(formattedData, { count: 0 }),
    [formattedData]
  );
  return (
    <Empty empty={emptyData}>
      <StreamChart
        className={className}
        onClickStream={onClickElement}
        onClickLegend={onClickLegend}
        series={formattedData}
        keys={map(facet.values, "label") as string[]}
        colors={colors}
        range={range}
        disableAnimation={disableAnimation}
        displayVolumes={dataviz.displayVolumes}
      />
    </Empty>
  );
}
